import { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { Storefront, CheckCircle, Block, HourglassBottom, LocalOffer, EmojiEvents, PeopleAlt, ConfirmationNumber, Redeem, PointOfSale, RateReview, History } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { adminPost } from "./adminApi";
import { consoleColors } from "./consoleTheme";
import { indiaDate } from "../../include/indiaDate";
import { formatCurrency } from "../../include/money";

const sections = [
  {
    title: "Stores",
    cards: [
      { key: "stores", label: "All stores", to: "/console/stores", icon: Storefront, tint: "#e4f4fd", color: "#21a7ec" },
      { key: "enabled", label: "Enabled", to: "/console/stores", icon: CheckCircle, tint: "#dcfce7", color: "#15803d" },
      { key: "disabled", label: "Disabled", to: "/console/stores", icon: Block, tint: "#fee2e2", color: "#b91c1c" },
      { key: "expiring", label: "Licence due in 15 days", to: "/console/stores", icon: HourglassBottom, tint: "#ffedd5", color: "#c2410c" },
    ],
  },
  {
    title: "OFFERS & ENGAGEMENT",
    cards: [
      { key: "coupons", label: "Coupons & Reward", to: "/console/coupons", icon: LocalOffer, tint: "#e0e7ff", color: "#4338ca" },
      { key: "luckydraw", label: "Lucky Draw Coupons", to: "/console/luckydraw", icon: EmojiEvents, tint: "#fef3c7", color: "#b45309" },
      { key: "customers", label: "Customers", to: "/console/customers", icon: PeopleAlt, tint: "#cffafe", color: "#0e7490" },
      { key: "reviews", label: "Customer Reviews", to: "/console/reviews", icon: RateReview, tint: "#fae8ff", color: "#a21caf" },
    ],
  },
  {
    title: "Activity",
    cards: [
      { key: "issued", label: "Issued coupons", to: "/console/issued", icon: ConfirmationNumber, tint: "#e4f4fd", color: "#21a7ec" },
      { key: "redeemed", label: "Redeemed coupons", to: "/console/redeemed", icon: Redeem, tint: "#dcfce7", color: "#15803d" },
      { key: "sales", label: "Sales", to: "/console/sales", icon: PointOfSale, tint: "#e0e7ff", color: "#4338ca" },
    ],
  },
];

export default function AdminHome() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const audit = Array.isArray(stats.audit) ? stats.audit : [];

  useEffect(() => {
    adminPost("ADMIN_DASHBOARD").then((result) => {
      if (result.status === "200") setStats(result.data || {});
    });
  }, []);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {sections.map((section) => (
        <Box key={section.title} sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: consoleColors.muted, letterSpacing: 0.4, mb: 1.2 }}>
            {section.title.toUpperCase()}
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 1.5 }}>
            {section.cards.map((card) => {
              const Icon = card.icon;
              return (
                <Box
                  key={card.key}
                  onClick={() => navigate(card.to)}
                  sx={{
                    background: "#fff",
                    border: `1px solid ${consoleColors.line}`,
                    borderRadius: 2.5,
                    p: 2,
                    cursor: "pointer",
                    display: "flex",
                    gap: 1.5,
                    alignItems: "center",
                    "&:hover": { borderColor: "#cbd5e1", boxShadow: "0 8px 20px rgba(15,23,42,0.06)" },
                  }}
                >
                  <Box sx={{ width: 42, height: 42, borderRadius: 2, background: card.tint, color: card.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon sx={{ fontSize: 22 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 24, fontWeight: 700, color: consoleColors.ink, lineHeight: 1 }}>{stats[card.key] ?? 0}</Typography>
                    <Typography sx={{ fontSize: 13, color: consoleColors.muted, mt: 0.4 }}>{card.label}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.2 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: consoleColors.muted, letterSpacing: 0.4 }}>
            AUDIT TRAIL
          </Typography>
          <Box
            onClick={() => navigate("/console/audit")}
            sx={{ display: "flex", alignItems: "center", gap: 0.6, cursor: "pointer", color: consoleColors.accent, fontSize: 13, fontWeight: 650 }}
          >
            <History sx={{ fontSize: 18 }} />
            View all
          </Box>
        </Box>
        <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, overflow: "hidden" }}>
          {audit.length ? audit.map((row) => (
            <Box key={row.audit_id} sx={{ px: 2, py: 1.2, borderBottom: `1px solid ${consoleColors.line}`, display: "grid", gridTemplateColumns: { xs: "1fr", md: "160px 1fr 140px" }, gap: 0.6 }}>
              <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>{indiaDate(row.date_added)}</Typography>
              <Box>
                <Typography sx={{ fontSize: 13.5, fontWeight: 650 }}>{row.action}</Typography>
                <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>
                  {[row.store_name, row.customer_name, row.comment].filter(Boolean).join(" · ")}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 13.5, fontWeight: 650, textAlign: { md: "right" } }}>
                {Number(row.amount) ? formatCurrency(row.amount) : ""}
              </Typography>
            </Box>
          )) : (
            <Typography sx={{ p: 2, color: consoleColors.muted, fontSize: 13.5 }}>No transactions yet. Sales, wallet cuts, coupons, and print requests will show here.</Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
